/// Tipos del lector de correo.
///
/// No extienden `Resource` ni usan el cliente JSON:API: los endpoints del buzón no son JSON:API, porque no hay
/// recurso persistido detrás. El buzón vive en el servidor de correo y GUAI solo hace de proxy en vivo.

export interface MailAddress {
  name?: string | null;
  address: string;
}

export type MailboxSpecialFolder = 'inbox' | 'sent' | 'drafts' | 'trash' | 'junk' | 'archive' | 'none';

export interface MailboxFolder {
  /// Ruta completa tal como la entiende el servidor. Es el identificador de la carpeta.
  path: string;
  name: string;
  delimiter: string;
  specialUse: MailboxSpecialFolder;
  total: number | null;
  unread: number | null;
  /// Generación de la carpeta. Viaja de vuelta en cada operación para que el servidor pueda rechazarla si la
  /// carpeta se ha reconstruido entretanto.
  uidValidity: number;
  selectable: boolean;
  children: MailboxFolder[];
}

/// Una carpeta en el diálogo de administración: lista plana con su estado de suscripción.
export interface MailboxManagedFolder {
  path: string;
  name: string;
  specialUse: MailboxSpecialFolder;
  subscribed: boolean;
  depth: number;
  total: number | null;
  unread: number | null;
  selectable: boolean;
  /// La entrada se muestra siempre, así que su suscripción no se puede cambiar.
  canChangeSubscription: boolean;
}

/// Nombre con el que se muestra una carpeta.
///
/// Los servidores devuelven el nombre canónico en inglés para las carpetas de siempre (INBOX es incluso parte del
/// protocolo), y Roundcube las traduce al idioma de la interfaz. Se hace lo mismo, comparando el nombre exacto y
/// no el papel de la carpeta: en esta cuenta conviven «Sent» y «Elementos enviados», y traducir por papel las
/// dejaría a las dos llamadas «Enviados».
/// Si tiene sentido contar mensajes sin leer en esta carpeta.
///
/// En Enviados y Borradores no lo tiene: son correos que ha escrito quien mira la pantalla, así que «sin leer» no
/// significa nada. El servidor devuelve el dato de todas formas —en un buzón real había 63 sin leer en Enviados—, y
/// mostrarlo solo invita a abrirlos para quitarse un aviso que no era tal.
///
/// En Papelera y Correo no deseado sí se cuenta: ahí puede haber correo recibido que nadie ha visto.
export function folderShowsUnread(folder: { specialUse: MailboxSpecialFolder }): boolean {
  return folder.specialUse !== 'sent' && folder.specialUse !== 'drafts';
}

/// Nombre visible según el papel que el servidor le da a la carpeta. Manda sobre la tabla de nombres.
const SPECIAL_DISPLAY_NAMES: Partial<Record<MailboxSpecialFolder, string>> = {
  'inbox': 'Entrada',
  'sent': 'Enviados',
  'drafts': 'Borradores',
  'trash': 'Papelera',
  'junk': 'Correo no deseado',
  'archive': 'Archivo'
};

/// Traducción por nombre, para las carpetas que no tienen ningún papel especial.
///
/// **Aquí no van los nombres de las carpetas especiales** (Sent, Trash, Drafts...): esos se traducen por el papel
/// que declara el servidor. Traducirlos por nombre hacía que una «Sent» creada por otro cliente saliera como
/// «Enviados» mientras la de verdad, «Elementos enviados», salía con su nombre crudo.
const FOLDER_DISPLAY_NAMES: Record<string, string> = {
  'outbox': 'Bandeja de salida',
  'notes': 'Notas',
  'tasks': 'Tareas',
  'calendar': 'Calendario',
  'contacts': 'Contactos',
  'journal': 'Diario'
};

export function folderDisplayName(name: string, specialUse: MailboxSpecialFolder = 'none'): string {
  if (!name) return '';

  //el papel que declara el servidor manda sobre el nombre: es lo único que distingue la carpeta de enviados de
  //verdad de otra que se llame parecido
  const porPapel = SPECIAL_DISPLAY_NAMES[specialUse];
  if (porPapel) return porPapel;

  return FOLDER_DISPLAY_NAMES[name.trim().toLowerCase()] ?? name;
}

export interface MailboxMessageSummary {
  uid: number;
  messageId?: string | null;

  /// Clave con la que se agrupan los mensajes de una conversación. La calcula el servidor: es el asunto sin los
  /// prefijos de respuesta ni reenvío, y el identificador del mensaje cuando no hay asunto.
  threadKey: string;
  date: Date;
  from?: MailAddress | null;
  to: MailAddress[];
  cc: MailAddress[];
  subject: string;
  size: number;
  seen: boolean;
  flagged: boolean;
  answered: boolean;
  /// Sale de la keyword IMAP `$Forwarded`, no de un flag de sistema: con buzones de Microsoft solo recoge los
  /// reenvíos hechos desde GUAI.
  forwarded: boolean;
  draft: boolean;
  hasAttachments: boolean;
  attachmentCount: number;
}

export interface MailboxAttachment {
  /// Identificador de la parte MIME ("2.1"), que es lo que el servidor entiende.
  partId: string;
  fileName: string;
  contentType: string;
  size: number;
  isInline: boolean;
  contentId?: string | null;
  downloadUrl: string;
}

export interface MailboxMessage extends MailboxMessageSummary {
  bcc: MailAddress[];
  replyTo: MailAddress[];
  /// Cuerpo ya saneado en el servidor. Nunca se pinta con [innerHTML]: va dentro del iframe aislado.
  htmlBody: string;
  /// Documento completo y autocontenido, con las imágenes del mensaje dentro como data:. Es la vía normal, y la
  /// que permite que el marco se ajuste al alto del correo. Vacío si el mensaje es demasiado grande.
  bodyDocument: string;
  /// Vía de reserva cuando el documento no cabe en la respuesta.
  bodyUrl: string;
  remoteContentBlocked: boolean;
  blockedImageCount: number;
  inReplyTo?: string | null;
  references: string[];
  attachments: MailboxAttachment[];
  hasCalendar: boolean;
  /// Generación de la carpeta al leer el mensaje. Se usa para las operaciones posteriores sobre ese mensaje, en
  /// lugar de la del listado, porque al abrir por enlace directo el listado puede no haber llegado todavía.
  uidValidity: number;
}

export interface MailboxMessageList {
  items: MailboxMessageSummary[];
  total: number;
  page: number;
  pageSize: number;
  folder: string;
  uidValidity: number;
  /// "sort" si ordenó el servidor por fecha; "uidOrder" si el servidor no soporta SORT y el orden es el de
  /// llegada, que es una aproximación. Office 365 no soporta SORT.
  sortSource: 'sort' | 'uidOrder';
  searchTruncated: boolean;
}

export interface MailboxSearch {
  text?: string;
  from?: string;
  subject?: string;
  unseenOnly?: boolean;
  flaggedOnly?: boolean;
  /// Buscar dentro del cuerpo. Apagado por defecto: sin índice de texto completo el servidor recorre el buzón
  /// entero y puede tardar minutos.
  searchBody?: boolean;
}

export interface MailboxDeleteResult {
  movedToFolder?: string | null;
  /// True cuando el buzón no tiene papelera y el borrado ha sido definitivo.
  permanentlyDeleted: boolean;
  affectedCount: number;
}

export type MailboxComposeMode = 'new' | 'reply' | 'replyAll' | 'forward';

/// Referencia al mensaje al que se responde o que se reenvía.
export interface MailboxMessageReference {
  folder: string;
  uid: number;
  uidValidity: number;
  messageId?: string | null;
}

/// Una parte del mensaje original que se reenvía tal cual.
///
/// Viaja como referencia: la relee el servidor del buzón al enviar, así que un adjunto grande no baja al navegador
/// para volver a subirlo.
export interface MailboxForwardedPart {
  partId: string;
  fileName: string;
  contentType: string;
  size: number;
}

/// Borrador preparado por el servidor, con destinatarios, asunto y la cita del original ya montada.
export interface MailboxDraft {
  mode: MailboxComposeMode;
  to: MailAddress[];
  cc: MailAddress[];
  subject: string;
  htmlBody: string;
  inReplyTo?: MailboxMessageReference | null;
  references: string[];
  forwardedParts: MailboxForwardedPart[];
}

export interface MailboxSendRequest {
  mode: MailboxComposeMode;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  htmlBody: string;
  attachments: { fileId: string }[];
  forwardedParts: MailboxForwardedPart[];
  inReplyTo?: MailboxMessageReference | null;
  references: string[];
}

export interface MailboxSendResult {
  messageId: string;
  /// Puede ser false sin que el envío haya fallado: guardar la copia es una operación aparte.
  sentAppended: boolean;
  answeredMarked: boolean;
  forwardedMarked: boolean;
  /// Lo que no salió perfecto pero no impidió enviar.
  warnings: string[];
}

/// Lo que se le pasa al compositor para abrirlo.
export interface MailboxComposeContext {
  mode: MailboxComposeMode;
  draft?: MailboxDraft | null;
}

export type MailboxErrorKind =
  | 'connection'
  | 'commandRejected'
  | 'timeout'
  | 'auth'
  | 'forbidden'
  | 'notFound'
  | 'staleFolder'
  | 'busy'
  | 'accountNotFound'
  | 'cancelled'
  | 'unknown';

/// Error del buzón ya clasificado.
///
/// Existe para que la interfaz pueda decidir qué mostrar sin leer textos, y para no depender del interceptor
/// global: los errores del buzón se pintan dentro del panel afectado, no como avisos flotantes.
export class MailboxError {
  constructor(
    public kind: MailboxErrorKind,
    public message: string,
    public retryable: boolean,
    public status: number
  ) {}
}

/// Los mensajes de una conversación, tal como los devuelve el servidor.
export interface MailboxConversation {
  messages: MailboxMessageSummary[];
  threadKey: string;

  /// Si se recortó por el tope de mensajes. Se avisa: una conversación incompleta sin decirlo engaña.
  truncated: boolean;
}

/// Estado de una conversación en el listado.
///
/// `messages` a null significa que solo se conocen los mensajes que venían en la página; en cuanto se despliega se
/// pide al servidor el hilo completo y se sustituye.
export interface MailboxThreadState {
  loading: boolean;
  truncated: boolean;
  messages: MailboxMessageSummary[] | null;
  error: string | null;
}

/// Una conversación ya montada para pintarla en el listado.
export interface MailboxThread {
  key: string;
  subject: string;
  participants: string;
  date: Date;
  count: number;
  complete: boolean;
  unread: boolean;
  flagged: boolean;
  hasAttachments: boolean;
  answered: boolean;
  forwarded: boolean;
  newest: MailboxMessageSummary;
  messages: MailboxMessageSummary[];
}

/// Fila del listado: o un mensaje, o la cabecera de una conversación.
export type MailboxListRow =
  | { kind: 'message'; message: MailboxMessageSummary; child: boolean }
  | { kind: 'thread'; thread: MailboxThread };
